/**
 * DerivationTrace — "How this was calculated" for a computed number.
 *
 * A number the drafter computed (calculate) or built into a table
 * (create_artefact) is in no source, so ordinary citations cannot point at
 * it. The server returns its derivation instead: the agent's reasoning
 * steps (naming inputs as {name}), the artefact operations, the method and
 * the result, plus each input with the citation of the source it was read
 * from. Every {name} renders as the input's value with an inline [n] chip;
 * clicking a chip opens that input's citation (document, page, quoted text).
 *
 * SRP: pure presentation of one derivation. Reused by the citation popover
 * (CitationDerivation) and the Action History row for calculate /
 * create_artefact (the shared ActionDerivation) — both satisfy TraceDerivation.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import React, { useState } from "react";
import type { Citation } from "../../api/client";
import { brandColors, fonts, inkColors, surfaceColors } from "../tokens";

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "6px 10px 8px",
    borderBottom: `1px solid ${surfaceColors.line}`,
    fontFamily: fonts.body,
  },
  title: {
    display: "flex",
    alignItems: "baseline",
    gap: "6px",
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    color: inkColors.ink4,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  result: {
    fontSize: "11px",
    fontWeight: 600,
    color: inkColors.ink,
  },
  unverified: {
    fontSize: "9.5px",
    fontFamily: fonts.mono,
    color: "#A4262C",
    textTransform: "none",
    letterSpacing: 0,
  },
  steps: {
    margin: 0,
    paddingLeft: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },
  step: {
    fontSize: "10.5px",
    lineHeight: 1.45,
    color: inkColors.ink2,
  },
  method: {
    fontSize: "10px",
    color: inkColors.ink3,
    fontStyle: "italic",
  },
  value: {
    fontWeight: 600,
    color: inkColors.ink,
  },
  chip: {
    fontSize: "9px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    color: brandColors.primary,
    background: "none",
    border: "none",
    padding: "0 1px",
    cursor: "pointer",
    verticalAlign: "super",
    "&:hover": { textDecoration: "underline" },
    "&:focus-visible": { outline: `2px solid ${brandColors.primary}`, outlineOffset: "1px" },
  },
  inputPanel: {
    marginTop: "2px",
    padding: "4px 8px",
    background: surfaceColors.paperDim,
    border: `1px solid ${surfaceColors.line}`,
    borderRadius: "3px",
    fontSize: "10px",
    color: inkColors.ink3,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  inputHead: {
    fontWeight: 600,
    color: inkColors.ink2,
  },
  warn: {
    color: "#A4262C",
  },
});

/** The citation fields the input panel reads. */
type TraceCitation = Pick<Citation, "document_name" | "section_name" | "page_no" | "context">;

/** One input as the trace reads it. */
export interface TraceInput {
  name: string;
  value?: unknown;
  kind: string;
  source: string;
  verified: boolean;
  reason: string;
  citation: TraceCitation | null;
}

/** One trace step as the trace reads it. */
export interface TraceStep {
  kind: string;
  text: string;
  input_refs?: string[];
  citation_numbers?: Record<string, number>;
}

/** What DerivationTrace reads of a derivation — the citation popover's
 *  CitationDerivation and Action History's ActionDerivation both fit. */
export interface TraceDerivation {
  method: string;
  value: string;
  unit: string;
  agent_note?: string;
  matched_values: string[];
  verified: boolean;
  trace: TraceStep[];
  inputs: TraceInput[];
}

/** A piece of a trace step: prose, or an input reference to render as value + chip. */
export type TraceSegment = { kind: "text"; text: string } | { kind: "input"; name: string };

/** Pure — exported for tests. Splits "CV {cv_percent} from …" into text and input segments. */
export function splitTraceText(text: string): TraceSegment[] {
  const out: TraceSegment[] = [];
  const re = /\{([A-Za-z_][A-Za-z0-9_]*)\}/g;
  let last = 0;
  for (let m = re.exec(text); m !== null; m = re.exec(text)) {
    if (m.index > last) out.push({ kind: "text", text: text.slice(last, m.index) });
    out.push({ kind: "input", name: m[1] });
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push({ kind: "text", text: text.slice(last) });
  return out;
}

/** Pure — exported for tests. How an input's value reads inline. */
export function formatInputValue(input: TraceInput | undefined, name: string): string {
  if (!input || input.value === undefined || input.value === null) return name;
  const v = input.value;
  if (Array.isArray(v)) return v.length > 4 ? `${v.slice(0, 4).join(", ")}, …` : v.join(", ");
  return String(v);
}

export interface DerivationTraceProps {
  derivation: TraceDerivation;
}

export const DerivationTrace: React.FC<DerivationTraceProps> = ({ derivation }) => {
  const styles = useStyles();
  const [openInput, setOpenInput] = useState<string | null>(null);
  const byName = new Map(derivation.inputs.map((i) => [i.name, i]));
  const reasons = derivation.trace.filter((s) => s.kind === "reason" || s.kind === "step");
  const method = derivation.trace.find((s) => s.kind === "method")?.text || derivation.method;
  const result =
    derivation.trace.find((s) => s.kind === "result")?.text ||
    (derivation.value ? `${derivation.value} ${derivation.unit}`.trim() : derivation.matched_values.join(", "));

  const renderStep = (step: TraceStep) =>
    splitTraceText(step.text).map((seg, i) => {
      if (seg.kind === "text") return <React.Fragment key={`t${i}`}>{seg.text}</React.Fragment>;
      const input = byName.get(seg.name);
      const n = step.citation_numbers?.[seg.name];
      return (
        <React.Fragment key={`i${i}`}>
          <span className={styles.value}>{formatInputValue(input, seg.name)}</span>
          {n !== undefined && (
            <button
              type="button"
              className={styles.chip}
              aria-label={`Source ${n} for ${seg.name}`}
              aria-expanded={openInput === seg.name}
              onClick={() => setOpenInput((cur) => (cur === seg.name ? null : seg.name))}
            >
              [{n}]
            </button>
          )}
          {input && !input.verified && input.kind !== "design" && <span className={styles.warn}> (unverified)</span>}
        </React.Fragment>
      );
    });

  // Artefact steps name input handles without {braces}: cite them after the step.
  const stepChips = (step: TraceStep) =>
    step.kind === "step"
      ? (step.input_refs ?? [])
          .filter((r) => step.citation_numbers?.[r] !== undefined && !step.text.includes(`{${r}}`))
          .map((r) => (
            <button
              key={r}
              type="button"
              className={styles.chip}
              aria-label={`Source for ${r}`}
              onClick={() => setOpenInput((cur) => (cur === r ? null : r))}
            >
              [{step.citation_numbers?.[r]}]
            </button>
          ))
      : null;

  const open = openInput ? byName.get(openInput) : undefined;
  const cit = open?.citation;

  return (
    <section className={styles.root} aria-label="How this was calculated">
      <div className={styles.title}>
        How this was calculated
        {!derivation.verified && <span className={styles.unverified}>· an input is not verified against its source</span>}
      </div>
      <div className={styles.result}>{result}</div>
      {reasons.length > 0 && (
        <ol className={styles.steps}>
          {reasons.map((step, i) => (
            <li key={`${step.kind}-${i}`} className={styles.step}>
              {renderStep(step)}
              {stepChips(step)}
            </li>
          ))}
        </ol>
      )}
      {method && <div className={styles.method}>Method: {method}</div>}
      {derivation.agent_note && <div className={styles.method}>Agent note: {derivation.agent_note}</div>}
      {open && (
        <div className={mergeClasses(styles.inputPanel)} role="region" aria-label={`Source of ${open.name}`}>
          <div className={styles.inputHead}>
            {open.name}
            {cit
              ? ` · ${cit.document_name}${cit.section_name ? ` · ${cit.section_name}` : ""}${cit.page_no > 0 ? ` · p.${cit.page_no}` : ""}`
              : open.source
                ? ` · ${open.source}`
                : ""}
          </div>
          {cit?.context && <div>{cit.context}</div>}
          {!open.verified && open.reason && <div className={styles.warn}>Not verified: {open.reason}</div>}
        </div>
      )}
    </section>
  );
};

export default DerivationTrace;
